import { type ApolloCache, ApolloError } from "@apollo/client";

import {
  LogisticsDhlAccountSettingsDocument,
  type LogisticsDhlAccountSettingsQuery,
  LogisticsDhlSetting,
  LogisticsDhlShippingSettingsDocument,
  type LogisticsDhlShippingSettingsQuery,
} from "../generated/graphql";

const settings = new Set<string>(Object.values(LogisticsDhlSetting));
const isSetting = (value: string): value is LogisticsDhlSetting => settings.has(value);

export const invalidDhlSettings = (error: unknown): LogisticsDhlSetting[] => {
  if (!(error instanceof ApolloError)) return [];
  return Array.from(
    new Set(
      error.graphQLErrors.flatMap(({ message }) => {
        const prefix = "dhl-settings-invalid:";
        if (!message.startsWith(prefix)) return [];
        const fields = message.slice(prefix.length).split(",");
        return fields.every(isSetting) ? fields : [];
      })
    )
  );
};

export const dhlSettingLabelKey = (setting: LogisticsDhlSetting): string => `dhlSettings.settings.${setting}`;

export const writeDhlShippingSettings = (
  cache: ApolloCache<unknown>,
  saved: LogisticsDhlShippingSettingsQuery["logisticsDhlShippingSettings"]
): void => {
  cache.writeQuery<LogisticsDhlShippingSettingsQuery>({
    query: LogisticsDhlShippingSettingsDocument,
    data: { logisticsDhlShippingSettings: saved },
  });
  const account = cache.readQuery<LogisticsDhlAccountSettingsQuery>({ query: LogisticsDhlAccountSettingsDocument });
  if (account)
    cache.writeQuery<LogisticsDhlAccountSettingsQuery>({
      query: LogisticsDhlAccountSettingsDocument,
      data: {
        logisticsDhlAccountSettings: { ...account.logisticsDhlAccountSettings, missingSettings: saved.missingSettings },
      },
    });
};

export const writeDhlAccountSettings = (
  cache: ApolloCache<unknown>,
  saved: LogisticsDhlAccountSettingsQuery["logisticsDhlAccountSettings"]
): void => {
  cache.writeQuery<LogisticsDhlAccountSettingsQuery>({
    query: LogisticsDhlAccountSettingsDocument,
    data: { logisticsDhlAccountSettings: saved },
  });
  const shipping = cache.readQuery<LogisticsDhlShippingSettingsQuery>({ query: LogisticsDhlShippingSettingsDocument });
  if (shipping)
    cache.writeQuery<LogisticsDhlShippingSettingsQuery>({
      query: LogisticsDhlShippingSettingsDocument,
      data: {
        logisticsDhlShippingSettings: {
          ...shipping.logisticsDhlShippingSettings,
          missingSettings: saved.missingSettings,
        },
      },
    });
};
